import {
  ChangeDetectorRef, ChangeDetectionStrategy,
  Component, EventEmitter, ElementRef, Input, Output,
  NgZone, OnInit, OnChanges, OnDestroy, ViewChild, forwardRef,
  SimpleChanges,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { BaseDropdownComponent } from '../_base-dropdown.component';
import { DropdownDirective } from '../dropdown.directive';
import { SelectDropdownMenuComponent } from './select-dropdown-menu/select-dropdown-menu.component';

import { keycodes } from '../../keycodes';
import { defaultOptionValue } from '../../form/select/option-helpers';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';


@Component({
  moduleId: module.id,
  selector: 'select-dropdown',
  templateUrl: 'select-dropdown.component.html',
  styleUrls: ['select-dropdown.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectDropdownComponent),
    multi: true,
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectDropdownComponent extends BaseDropdownComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() items: Array<any> = [];
  @Input() alignmentRight: boolean = false;
  @Input() declare placeholder: string;
  @Input() placeholderOnly: boolean = false;
  @Input() ghostValue: string;
  @Input() optionGhostValue: string;
  @Input() hasSearch: boolean = false;
  @Input() disabled: boolean = false;
  @Input() disabledText: string;
  @Input() hasError: boolean = false;
  @Input() isVersion2: boolean = false;
  @Input() validateOnBlur: boolean = false;
  @Input() allowClear: boolean = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('validateFormControl') validateFormControl: any;
  @Input() declare selectedItem: any;
  @Input() hideSelectedValue: boolean = false;
  @Input() isLanguage: boolean = false;
  // if option from this array is selected then we show placeholder instead of it
  @Input() placeholderOptions: Array<any>;
  @Input() isMultiple: boolean = false;
  @Input() selectedItems: Array<any> = [];

  @Output() selectedItemChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() dropdownOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clearValue: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(DropdownDirective, {static: true})
  dropdownWrap: DropdownDirective | void;

  @ViewChild('searchInput', {static: false})
  searchInput: ElementRef | void;

  @ViewChild('dropdownMenu', {static: false})
  dropdownMenu: SelectDropdownMenuComponent | void;

  searchTerm: string;
  selectedOption: string;
  selectedOptions: Array<string> = [];
  private dropdownOpenSub: Subscription;
  private keydownSub: Subscription;

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get isOpened(): boolean {
    return this.dropdownWrap && this.dropdownWrap.isOpened;
  }

  get filteredItems(): Array<any> {
    if (!this.items) { return null; }
    if (!this.searchTerm) { return this.items; }
    if (!_.isArray(this.items)) {
      return null;
    }
    return this.items.filter((item: any) => {
      return String(item.title).toLowerCase()
      .indexOf(this.searchTerm.toLowerCase()) !== -1;
    });
  }

  get isShowPlaceholder(): boolean {
    if (this.isMultiple) {
      return !this.placeholderOnly && !(this.disabled && this.disabledText) && !this.selectedItems.length;
    }
    return (!this.placeholderOnly &&
      !(this.disabled && this.disabledText) &&
      !(_.get(this.selectedItem, 'name') || _.get(this.selectedItem, 'title')));
  }

  get showClearIcon(): boolean {
    if (!this.allowClear || this.disabled) { return false; }
    return !_.includes([0, defaultOptionValue], _.get(this, 'selectedItem.option', defaultOptionValue));
  }

  get hasSelectedItem(): boolean {
    if (this.isMultiple) { return !!this.selectedItems.length; }
    return !_.isEmpty(this.selectedItem);
  }

  // E.g. if { option: '', title: '-' } is selected then show placeholder instead of '-'
  // options should be provided in the placeholderOptions array
  get isOptionPlaceholder(): boolean {
    if (!this.placeholderOptions) { return false; }
    return _.includes(this.placeholderOptions, _.get(this.selectedItem, 'option'));
  }

  get currentValue(): string {
    if  (this.isMultiple) {
      return _.join(this.selectedItems.map(i => i.name || i.title), ', ');
    }
    return this.selectedItem.name || this.selectedItem.title;
  }

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef,
    private ref: ChangeDetectorRef,
  ) {
    super();
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    this.subscribeDropdownOpen();
    super.ngOnInit();
    if (this.isMultiple) { this.selectedOptions = this.selectedItems.map(i => i.option); }
    if (this.selectedItem) {
      this.selectedOption = this.selectedItem.option;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // if items is passed after selected option was passed
    if (changes.items) {
      if (this.selectedOption && !this.selectedItem) {
        this.setSelectedItem(this.selectedOption);
      }
      if (this.isMultiple) {
        this.setSelectedItems(this.selectedOptions);
      }
      // hover first item in the list
      _.defer(() => {
        if ((!this.selectedOption || this.isMultiple && !this.selectedOptions.length) && this.dropdownMenu) {
          this.dropdownMenu.hoverFirstItem();
        }
      });
    }
    if (changes.selectedItem) {
      this.selectedOption = _.get(changes.selectedItem, 'currentValue.option');
    }
  }

  ngOnDestroy() {
    if (this.dropdownOpenSub) { this.dropdownOpenSub.unsubscribe(); }
    this.unsubscribeKeydownEvent();
    super.ngOnDestroy();
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onSelectedChange($event: any) {
    if (this.isMultiple) {
      this.selectedOptions = $event.map(i => i.option);
      this.setSelectedItems(this.selectedOptions);
    } else {
      this.setSelectedItem($event.option);
      this.selectedOption = $event.option;
    }
    // change events for form control
    this.propagateChange($event.option);
    this.selectedItemChange.emit($event);
    super.onSelectedChange($event);
  }

  onSearchTermChange(value: string) {
    this.searchTerm = value;
    _.defer(() => this.dropdownMenu && this.dropdownMenu.hoverFirstItem());
  }

  onInputFocus() {
    _.defer(() => {
      if (this.dropdownWrap) {
        this.dropdownWrap.show();
      }
      this.ref.markForCheck();
    });
  }

  onClearValueClick($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.writeValue(null);
    this.clearValue.emit();
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  // implemented from ControlValueAccessor
  public writeValue(option: any): void {
    if (this.isMultiple) {
      this.selectedOptions = option; // Array<string>
      this.setSelectedItems(this.selectedOptions);
      return;
    }
    this.selectedOption = option;
    this.setSelectedItem(option);
  }

  // implemented from ControlValueAccessor
  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  // implemented from ControlValueAccessor
  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public toggleDropdown(): void {
    _.defer(() => {
      if (this.dropdownWrap) {
        this.dropdownWrap.toggle();
      }
      this.ref.markForCheck();
    });
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/
   /* eslint-disable */
  // the method set in registerOnChange, it is just
  // a placeholder for a method that takes one parameter,
  // we use it to emit changes back to the form
  private propagateChange = (_: any) => { };

  // the method set in registerOnTouch, it is just
  // a placeholder for a method that takes one parameter,
  // we use it to emit changes back to the form
  private propagateTouch = (_: any) => { };
  /* eslint-enable */

  private subscribeDropdownOpen(): void {
    this.dropdownOpenSub = this.dropdownWrap && this.dropdownWrap.dropdownOpen
    .subscribe((isOpened: boolean) => {
      if (isOpened) {
        if (this.hasSearch) {
          _.delay(() => {
            if (this.searchInput) {
              this.searchInput.nativeElement.focus();
            }
          }, 500);
        }
        if (this.selectedOption) {
          const selectedItem = _.find(this.items, ['option', this.selectedOption]);
          // if selected item is not in the options list or it's hidden then hover first option
          if ((!selectedItem || selectedItem.hidden) && this.dropdownMenu) {
            this.dropdownMenu.setHoveredItem(_.head(this.items));
          }
        }
        this.subscribeKeydownEvent();
      } else {
        this.unsubscribeKeydownEvent();
        if (this.hasSearch) {
          _.delay(() => {
            this.searchTerm = '';
          }, 300);
        }
      }
    });
  }

  private subscribeKeydownEvent(): void {
    this.ngZone.runOutsideAngular(() => {
      this.keydownSub = fromEvent(window, 'keydown')
      .subscribe((event: any) => {
        if (!_.get(this.items, 'length')) {
          if (event.keyCode === keycodes.ESC || event.keyCode === keycodes.TAB) {
            event.preventDefault();
            if (this.dropdownWrap) {
              this.dropdownWrap.close();
            }
          }
          return;
        }
        if (event.keyCode === keycodes.DOWN_ARROW) {
          event.preventDefault();
          if (this.dropdownMenu) {
            this.dropdownMenu.hoverNextItem(1);
          }
        } else if (event.keyCode === keycodes.UP_ARROW) {
          event.preventDefault();
          if (this.dropdownMenu) {
            this.dropdownMenu.hoverNextItem(-1);
          }
        } else if (event.keyCode === keycodes.ENTER || event.keyCode === keycodes.TAB) {
          event.preventDefault();
          if (this.dropdownMenu) {
            this.dropdownMenu.selectHoveredItem();
          }
        } else if (event.keyCode === keycodes.ESC) {
          event.preventDefault();
          if (this.dropdownWrap) {
            this.dropdownWrap.close();
          }
        }
      });
    });
  }

  private unsubscribeKeydownEvent(): void {
    if (this.keydownSub) { this.keydownSub.unsubscribe(); }
  }

  private setSelectedItem(option: string) {
    if (!this.items) { return; }
    const selectedItem = this.items.find((item: any) => item.option === option);
    this.selectedItem = selectedItem;
    if (this.dropdownMenu) {
      this.dropdownMenu.setHoveredItem(null);
    }
    this.ref.markForCheck();
  }

  private setSelectedItems(options: Array<string>) {
    if (!this.items) { return; }
    const selectedItems = this.items.filter((item: any) => options.includes(item.option));
    this.selectedItems = selectedItems;
    if (this.dropdownMenu) {
      this.dropdownMenu.setHoveredItem(null);
    }
    this.ref.markForCheck();
  }

}
